import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiBriefcase, FiSearch, FiMoreVertical, FiEdit2, FiCreditCard, FiDollarSign, FiSettings } from 'react-icons/fi';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { CompanyEditDrawer } from '../components/CompanyEditDrawer';
import { PaymentDrawer } from '../components/PaymentDrawer';
import { SubscriptionDrawer } from '../components/SubscriptionDrawer';
import { DropdownMenu } from '@/shared/components/ui/DropdownMenu';

interface Company {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  status: string;
  subscriptionStatus: string | null;
  creditBalance: number;
  nextRenewalAt?: string | null;
  hasCard?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CompaniesResponse {
  success: boolean;
  data: Company[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const CompaniesPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [payingCompany, setPayingCompany] = useState<Company | null>(null);
  const [managingCompany, setManagingCompany] = useState<Company | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-companies', page, search],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      const response = await api.get<CompaniesResponse>(`/companies?${params}`);
      return response.data;
    },
  });

  const companies = data?.data || [];
  const pagination = data?.pagination;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  const subStatusBadge = (s: string | null): { label: string; variant: 'success' | 'warning' | 'error' | 'default' } => {
    switch (s) {
      case 'active': return { label: 'Ativa', variant: 'success' };
      case 'past_due': return { label: 'Inadimplente', variant: 'warning' };
      case 'suspended': return { label: 'Suspensa', variant: 'error' };
      case 'awaiting_payment': return { label: 'Aguardando', variant: 'warning' };
      case 'pending': return { label: 'Pendente', variant: 'default' };
      case 'cancelled': return { label: 'Cancelada', variant: 'error' };
      default: return { label: 'Sem assinatura', variant: 'default' };
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold" style={{ color: 'var(--theme-card-text)' }}>Companies</h1>
        <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Gerencie as companies do seu app</p>
      </div>

      {/* Search */}
      <div className="mb-4 max-w-sm">
        <Input
          placeholder="Buscar por nome ou email..."
          icon={FiSearch}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={20} />
              ))}
            </div>
          ) : companies.length === 0 ? (
            <div className="flex flex-col items-center text-center p-12">
              <div
                className="flex items-center justify-center w-12 h-12 rounded-xl mb-4"
                style={{ backgroundColor: 'var(--theme-primary-background)' }}
              >
                <FiBriefcase className="h-6 w-6" style={{ color: 'var(--theme-primary)' }} />
              </div>
              <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--theme-card-text)' }}>
                Nenhuma company encontrada
              </h3>
              <p className="text-sm max-w-sm" style={{ color: 'var(--theme-muted-text)' }}>
                {search ? 'Tente ajustar o termo de busca.' : 'Nenhuma company foi registrada neste app ainda.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                    {['Nome', 'Telefone', 'Status', 'Assinatura', 'Créditos', 'Criado em', ''].map((h) => (
                      <th
                        key={h}
                        className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company, index) => (
                    <tr
                      key={company.id}
                      className={`${index > 0 ? 'border-t' : ''} transition-colors`}
                      style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {/* Nome + email */}
                      <td className="px-4 py-3">
                        <div className="font-medium" style={{ color: 'var(--theme-card-text)' }}>
                          {company.name}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                          {company.email || '-'}
                        </div>
                      </td>
                      {/* Telefone */}
                      <td className="px-4 py-3" style={{ color: 'var(--theme-muted-text)' }}>
                        {company.phone || '-'}
                      </td>
                      {/* Status */}
                      <td className="px-4 py-3">
                        <Badge variant={company.status === 'active' ? 'success' : 'error'}>
                          {company.status === 'active' ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      {/* Assinatura + data de renovação + ícone de cartão */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {(() => { const b = subStatusBadge(company.subscriptionStatus); return (
                            <Badge variant={b.variant}>{b.label}</Badge>
                          ); })()}
                          {company.hasCard && (
                            <FiCreditCard
                              className="w-4 h-4"
                              style={{ color: 'var(--theme-muted-text)' }}
                              title="Cartão cadastrado"
                            />
                          )}
                        </div>
                        {company.nextRenewalAt && (
                          <div className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                            Renova em {formatDate(company.nextRenewalAt)}
                          </div>
                        )}
                      </td>
                      {/* Créditos */}
                      <td className="px-4 py-3 font-medium" style={{ color: 'var(--theme-card-text)' }}>
                        {company.creditBalance.toLocaleString('pt-BR')}
                      </td>
                      {/* Criado em */}
                      <td className="px-4 py-3" style={{ color: 'var(--theme-muted-text)' }}>
                        {formatDate(company.createdAt)}
                      </td>
                      {/* Ações */}
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu
                          align="right"
                          trigger={<FiMoreVertical className="w-4 h-4" />}
                          items={[
                            {
                              label: 'Assinatura',
                              icon: <FiSettings className="w-4 h-4" />,
                              onClick: () => setManagingCompany(company),
                            },
                            {
                              label: 'Editar',
                              icon: <FiEdit2 className="w-4 h-4" />,
                              onClick: () => setEditingId(company.id),
                            },
                            {
                              label: 'Efetuar pagamento',
                              icon: <FiDollarSign className="w-4 h-4" />,
                              hidden: !(company.subscriptionStatus === 'active' && company.hasCard),
                              onClick: () => setPayingCompany(company),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            {pagination.total} companies - Pagina {pagination.page} de {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm rounded-lg border disabled:opacity-40 transition-colors"
              style={{
                borderColor: 'var(--theme-card-border)',
                color: 'var(--theme-card-text)',
                backgroundColor: 'var(--theme-card-background)',
              }}
            >
              Anterior
            </button>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page >= pagination.totalPages}
              className="px-3 py-1.5 text-sm rounded-lg border disabled:opacity-40 transition-colors"
              style={{
                borderColor: 'var(--theme-card-border)',
                color: 'var(--theme-card-text)',
                backgroundColor: 'var(--theme-card-background)',
              }}
            >
              Proximo
            </button>
          </div>
        </div>
      )}
      <CompanyEditDrawer
        companyId={editingId}
        isOpen={!!editingId}
        onClose={() => setEditingId(null)}
      />
      <PaymentDrawer
        company={payingCompany ? { id: payingCompany.id, name: payingCompany.name } : null}
        isOpen={!!payingCompany}
        onClose={() => setPayingCompany(null)}
      />
      <SubscriptionDrawer
        company={managingCompany ? { id: managingCompany.id, name: managingCompany.name } : null}
        isOpen={!!managingCompany}
        onClose={() => setManagingCompany(null)}
      />
    </div>
  );
};
